import type {
  CarsWithSalesItemsForBookingResponseRecord,
  LicensePlate,
  SalesItemBookingCategoryType,
  TireSeason
} from '@navio/nsdk';
import {
  SALES_ITEM_BOOKING_FLOW_TYPE,
  type SelectedSalesItemsCar,
  type ServiceItemsMappedForFleetWithCars,
  type TireHotelPickupDecision
} from 'noddi-types';
import { compareLicensePlates } from 'noddi-util';

export const getUniqueSalesItems = (
  data: CarsWithSalesItemsForBookingResponseRecord
): ServiceItemsMappedForFleetWithCars[] =>
  Object.values(
    data.cars.reduce<Record<string, ServiceItemsMappedForFleetWithCars>>((acc, car) => {
      car.salesItems.forEach((item) => {
        const {
          salesItemId,
          isAddonsAvailable,
          isWheelPickupRequired,
          isIncomingTireSeasonRequired,
          mutuallyExclusiveSalesItemIds,
          name,
          description,
          uiSortOrder,
          shortDescription,
          bookingCategoryType: rawBookingCategoryType,
          temporarilyUnavailable
        } = item;
        const bookingCategoryType = rawBookingCategoryType as SalesItemBookingCategoryType | null;
        // CU-86c26e3jq
        // Todo - this is not clear. Now it just sets the first one found when mapping. Maybe change name to groupedDescription, groupedShortDescription, priceFrom?
        if (!acc[item.name]) {
          acc[item.name] = {
            name,
            description,
            uiSortOrder,
            price: item.price.priceGross,
            shortDescription,
            bookingCategoryType,
            cars: []
          };
        }
        acc[item.name]?.cars.push({
          ...car,
          salesItemId,
          isAddonsAvailable,
          isWheelPickupRequired,
          isIncomingTireSeasonRequired,
          mutuallyExclusiveSalesItemIds,
          bookingCategoryType: bookingCategoryType as SalesItemBookingCategoryType,
          price: item.price.priceGross,
          temporarilyUnavailable
        });
      });
      return acc;
    }, {})
  );

export const numberOfSelectedItemsFleets = ({
  salesItemsInCategory,
  selectedSalesItems
}: {
  salesItemsInCategory: ServiceItemsMappedForFleetWithCars[];
  selectedSalesItems: SelectedSalesItemsCar[];
}) =>
  salesItemsInCategory.reduce((acc, item) => {
    const reducedServices = item.cars.reduce((acc, car) => {
      const selectedSalesItemsCar = selectedSalesItems.find(
        (selectedSalesItemCar) =>
          selectedSalesItemCar.salesItemId === car.salesItemId &&
          selectedSalesItemCar.licensePlate.number === car.licensePlate.number
      );

      return selectedSalesItemsCar ? acc + 1 : acc;
    }, 0);

    return acc + reducedServices;
  }, 0);

interface SalesItemToRemove {
  licensePlate: SelectedSalesItemsCar['licensePlate'];
  salesItemId: SelectedSalesItemsCar['salesItemId'];
  type: SelectedSalesItemsCar['type'];
}

const getOnlyPrimarySalesItemsForLicensePlate = ({
  selectedSalesItemsCars,
  licensePlate,
  salesItemId
}: {
  selectedSalesItemsCars: SelectedSalesItemsCar[];
  licensePlate: SelectedSalesItemsCar['licensePlate'];
  salesItemId: SelectedSalesItemsCar['salesItemId'];
}) =>
  selectedSalesItemsCars.filter((item) => {
    if (!compareLicensePlates(item.licensePlate, licensePlate)) {
      return true;
    }
    // Admin extras are picked outside the customer-facing lists, so a primary change must not drop them.
    if (item.type === SALES_ITEM_BOOKING_FLOW_TYPE.ADMIN_EXTRA) {
      return true;
    }
    return item.type === SALES_ITEM_BOOKING_FLOW_TYPE.PRIMARY && item.salesItemId !== salesItemId;
  });

export const removeSalesItemConstructor = ({
  selectedSalesItemsCars,
  itemToRemove
}: {
  selectedSalesItemsCars: SelectedSalesItemsCar[];
  itemToRemove: SalesItemToRemove;
}) => {
  const { type, licensePlate, salesItemId } = itemToRemove;
  if (type !== SALES_ITEM_BOOKING_FLOW_TYPE.PRIMARY) {
    const salesItemsExcludingCurrent = selectedSalesItemsCars.filter(
      (item) => !(compareLicensePlates(item.licensePlate, licensePlate) && item.salesItemId === salesItemId)
    );

    return {
      selectedSalesItemsCars: salesItemsExcludingCurrent,
      selectedTimeWindow: undefined
    };
  }

  // Remove all addons for the license plate when removing a primary sales item
  const filteredItems = getOnlyPrimarySalesItemsForLicensePlate({
    selectedSalesItemsCars,
    licensePlate,
    salesItemId
  });

  return {
    selectedSalesItemsCars: filteredItems,
    selectedTimeWindow: undefined
  };
};

export const removeMultipleSalesItemsConstructor = ({
  selectedSalesItemsCars,
  itemsToRemove
}: {
  selectedSalesItemsCars: SelectedSalesItemsCar[];
  itemsToRemove: SalesItemToRemove[];
}) => {
  const updatedSalesItems = itemsToRemove.reduce(
    (acc, { type, licensePlate, salesItemId }) => {
      if (type !== SALES_ITEM_BOOKING_FLOW_TYPE.PRIMARY) {
        return acc.filter(
          (item) => !(compareLicensePlates(item.licensePlate, licensePlate) && item.salesItemId === salesItemId)
        );
      } else {
        // Remove all addons for the license plate when removing a primary sales item
        return getOnlyPrimarySalesItemsForLicensePlate({
          selectedSalesItemsCars: acc,
          licensePlate,
          salesItemId
        });
      }
    },
    [...selectedSalesItemsCars]
  );

  return {
    selectedSalesItemsCars: updatedSalesItems,
    selectedTimeWindow: undefined
  };
};

export const getNewSalesItemsInState = ({
  selectedSalesItemsCars,
  salesItemToAdd,
  mutuallyExclusiveSalesItemIds
}: {
  selectedSalesItemsCars: SelectedSalesItemsCar[];
  salesItemToAdd: SelectedSalesItemsCar;
  mutuallyExclusiveSalesItemIds: number[];
}) => {
  const preppedItems =
    salesItemToAdd.type === SALES_ITEM_BOOKING_FLOW_TYPE.PRIMARY
      ? getOnlyPrimarySalesItemsForLicensePlate({
          selectedSalesItemsCars,
          salesItemId: salesItemToAdd.salesItemId,
          licensePlate: salesItemToAdd.licensePlate
        })
      : selectedSalesItemsCars;

  const filteredItems = preppedItems.filter(
    (item) =>
      !(
        compareLicensePlates(item.licensePlate, salesItemToAdd.licensePlate) &&
        mutuallyExclusiveSalesItemIds.includes(item.salesItemId)
      )
  );

  return [...filteredItems, salesItemToAdd];
};

/** Custom pickup address (no linked location) the customer has not yet confirmed or declined a suggested location for. */
export const getPickupAddressAwaitingTireHotelDecision = (car: SelectedSalesItemsCar | undefined) =>
  car?.metaData?.selectedTireHotelLocation || car?.metaData?.tireHotelPickupDecision
    ? undefined
    : car?.metaData?.selectedTireHotelPickUpAddress;

/** Customer's answer to the tire hotel location suggestion, applied to every car awaiting one (the backend error does not identify the car). */
export const applyTireHotelPickupDecision = (
  selectedSalesItemsCars: SelectedSalesItemsCar[],
  tireHotelPickupDecision: TireHotelPickupDecision
): SelectedSalesItemsCar[] =>
  selectedSalesItemsCars.map((car) => {
    const selectedTireHotelPickUpAddress = getPickupAddressAwaitingTireHotelDecision(car);
    return selectedTireHotelPickUpAddress
      ? { ...car, metaData: { selectedTireHotelPickUpAddress, tireHotelPickupDecision } }
      : car;
  });

/** The wheel set the customer chose to store, set on the car's rows whose item asks for it. */
export const applyIncomingTireSeason = (
  selectedSalesItemsCars: SelectedSalesItemsCar[],
  { licensePlate, season }: { licensePlate: LicensePlate; season: TireSeason }
): SelectedSalesItemsCar[] =>
  selectedSalesItemsCars.map((item) =>
    item.isIncomingTireSeasonRequired && compareLicensePlates(item.licensePlate, licensePlate)
      ? { ...item, metaData: { ...item.metaData, incomingTireSeason: season } }
      : item
  );
